import React, { useEffect, useRef, useState } from "react";
import { FieldArray } from "formik";
import InputField from "../../Utils/InputField";
import SelectField from "../../Utils/SelectField";
import {
  Row,
  Col,
  Label,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
} from "reactstrap";
import { handleChangeForImsi } from "../../Customers/constants/HandleChangeFunctions";
import BalanceImpactTableRecurring from "./BalanceImpactTableRecurring";
import AttributeBasedDetailsTableRecurring from "./AttributeBasedDetailsTableRecurring";
import { useDispatch, useSelector } from "react-redux";
import { getAllAttribute } from "../../../slices/services/addservice/provisionalattribute/thunk";
import { tabChange } from "../../Utils/SetDefaultRowData";
import BalanceImpactList from "./BalanceImpactList";
import { isEmptyOrInvalidBalanceImpactRecurringField } from "./constants/checkEmptyFields";
import { CycleAlignmentOption } from "./constants/initialValues";
import { LabelText } from "../../Utils/Input";
import { CreatableSelectField } from "../../Utils/CreatableSelectField";
import { addUsageEventType } from "../../../slices/usageEventType/addUsageEventType/thunk";
import { fetchAllUsageEventType } from "../../../slices/usageEventType/thunk";

const UsageRatePlanRowRecurring = ({
  index,
  val,
  isEdit,
  isSubscribed,
  values,
  errors,
  touched,
  handleChange,
  handleGrantsAndChargesResourceValueAndUsageFields,
  handleGrantsAndChargesRecurringFrequencyAndUsageOptions,
  resourceNameOptions,
  uomOptions,
  pricingModelOptions,
  togglePricingModel,
  remove,
  setFieldValue,
  setFieldTouched,
  toggleBalanceDropdown,
  toggleAttributeDropdown,
  balanceDropdown,
  attributeDropdown,
  setTouched,
  isSubmitting,
}) => {
  const [openAccordion, setOpenAccordion] = React.useState("");
  const [loading, setLoading] = useState();
  const [usageRateTab, setUsageRateTab] = useState("1");
  const pushBalanceRef = useRef(null);

  const dispatch = useDispatch();
  const attributes = useSelector((state) => state.Provisional.provisional);
  const usageEventTypeList = useSelector(
    (state) => state?.UsageEventType?.usageEventType
  );
  const EventTypeOptions =
    usageEventTypeList
      ?.map((i) => {
        return i?.name ? { label: i?.name, value: i?.name } : null;
      })
      .filter(Boolean) || [];

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion !== id ? id : "");
  };

  const handleAddRow = () => {
    setFieldValue(`recurringUsageRatePlan[${index}].balanceImpacts`, [
      ...values?.recurringUsageRatePlan?.[index].balanceImpacts,
      {
        resourceName: "",
        flatRate: "",
        validityStart: "",
        validityEnd: "",
        pricingModel: "",
        pricingModelNew: [
          {
            startingUnit: "",
            endingUnit: "",
            pricePerUnit: "",
          },
        ],
        perUnitRate: "",
        startDateTime: null,
        timeUnit: "",
        endDateTime: null,
        perUnitRate: "",
        recurringFrequency: "",
        recurringFrequencyUnit: 1,
        cycleAlignment: CycleAlignmentOption[1],
      },
    ]);
    setTouched((prev) => ({
      ...prev,
      recurringUsageRatePlan: prev.recurringUsageRatePlan?.map((plan, idx) => {
        if (idx === index) {
          return {
            ...plan,
            balanceImpacts: [
              ...(plan.balanceImpacts || []),
              {
                resourceName: false,
                flatRate: false,
                validityStart: false,
                validityEnd: false,
                pricingModel: false,
                pricingModelNew: [
                  {
                    startingUnit: false,
                    endingUnit: false,
                    pricePerUnit: false,
                  },
                ],
                perUnitRate: false,
                startDateTime: false,
                timeUnit: false,
                endDateTime: false,
                timeValue: false,
                recurringFrequency: false,
                cycleAlignment: false,
              },
            ],
          };
        }
        return plan;
      }),
    }));
  };

  // Validation function to check if current attribute details are valid
  const validateCurrentAttributeDetails = () => {
    const currentAttributeDetails =
      values.recurringUsageRatePlan?.[index]?.attributeBasedDetails || [];

    for (
      let detailIndex = 0;
      detailIndex < currentAttributeDetails.length;
      detailIndex++
    ) {
      const detail = currentAttributeDetails[detailIndex];

      // Check required fields for attribute detail
      if (!detail.ruleId || !detail.name || !detail.logicalOperator) {
        return false;
      }

      // Check conditions
      if (!detail.conditions || detail.conditions.length === 0) {
        return false;
      }

      // Check each condition
      for (
        let conditionIndex = 0;
        conditionIndex < detail.conditions.length;
        conditionIndex++
      ) {
        const condition = detail.conditions[conditionIndex];
        if (
          !condition.attributeName ||
          !condition.operator ||
          !condition.value
        ) {
          return false;
        }
      }
    }

    return true;
  };

  const addAttributeBasedDetail = () => {
    // Validate existing details before adding new one
    const tableDataRecurring = val.attributeBasedDetails || [];
    if (tableDataRecurring.length > 0 && !validateCurrentAttributeDetails()) {
      // Set touched state for validation display
      const newTouched = { ...touched };
      if (!newTouched.recurringUsageRatePlan)
        newTouched.recurringUsageRatePlan = {};
      if (!newTouched.recurringUsageRatePlan?.[index])
        newTouched.recurringUsageRatePlan[index] = {};
      if (!newTouched.recurringUsageRatePlan?.[index].attributeBasedDetails) {
        newTouched.recurringUsageRatePlan[index].attributeBasedDetails = [];
      }

      // Mark all existing details as touched
      tableDataRecurring.forEach((_, detailIndex) => {
        newTouched.recurringUsageRatePlan[index].attributeBasedDetails[
          detailIndex
        ] = {
          ruleId: true,
          name: true,
          logicalOperator: true,
          conditions:
            tableDataRecurring[detailIndex].conditions?.map(() => ({
              attributeName: true,
              operator: true,
              value: true,
            })) || [],
        };
      });

      setTouched(newTouched);
      return; // Don't add new detail if validation fails
    }

    const newDetail = {
      ruleId: "",
      name: "",
      logicalOperator: "",
      conditions: [
        {
          attributeName: "",
          operator: "",
          value: "",
        },
      ],
    };

    const updatedData = [...tableDataRecurring, newDetail];
    // setTableDataRecurring(updatedData);

    // Update Formik values
    setFieldValue(
      `recurringUsageRatePlan[${index}].attributeBasedDetails`,
      updatedData
    );
  };

  useEffect(() => {
    dispatch(getAllAttribute(setLoading));
  }, [dispatch]);

  return (
    <div>
      <div className="rv-nav d-flex flex-wrap flex-lg-nowrap align-items-center">
        <Nav role="tablist" className="ms-3 me-1 flex-grow-1 d-flex flex-wrap">
          <NavItem>
            <NavLink
              className={usageRateTab === "1" ? "active" : ""}
              onClick={() => {
                tabChange("1", usageRateTab, setUsageRateTab);
              }}
              type="button"
            >
              <i className="fas fa-home"></i>
              Details
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="#"
              className={usageRateTab === "2" ? "active" : ""}
              onClick={() => {
                tabChange("2", usageRateTab, setUsageRateTab);
              }}
              type="button"
            >
              <i className="far fa-user"></i>
              Balance Impact Details
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={usageRateTab === "3" ? "active" : ""}
              onClick={() => {
                tabChange("3", usageRateTab, setUsageRateTab);
              }}
              type="button"
            >
              <i className="fas fa-home"></i>
              Attribute Based Details
            </NavLink>
          </NavItem>
          {usageRateTab === "2" && !isSubscribed && (
            <Button
              color="secondary"
              className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-0 text-nowrap mb-1 ms-lg-auto"
              onClick={() => {
                const lastIndex =
                  values?.recurringUsageRatePlan?.[index]?.balanceImpacts
                    .length - 1;
                if (
                  lastIndex === -1 ||
                  !isEmptyOrInvalidBalanceImpactRecurringField(
                    index,
                    values,
                    lastIndex
                  )
                ) {
                  handleAddRow();
                }
              }}
            >
              <i className="ri-add-fill label-icon align-middle fs-16 me-2"></i>
              <span className="px-0 ps-2 ms-2 me-1">
                Add Balance Impact Details
              </span>
            </Button>
          )}
          {usageRateTab === "3" && !isSubscribed && (
            <Button
              color="secondary"
              className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-0 text-nowrap mb-1 ms-lg-auto"
              onClick={() => {
                if (validateCurrentAttributeDetails()) {
                  addAttributeBasedDetail();
                }
              }}
            >
              <i className="ri-add-fill label-icon align-middle fs-16 me-2"></i>
              <span className="px-0 ps-2 ms-2 me-1">
                Add Attribute Based Details
              </span>
            </Button>
          )}
        </Nav>
      </div>

      <TabContent activeTab={usageRateTab}>
        <TabPane tabId={"1"}>
          <Row className="mx-2 my-3">
            <Col xs={12} sm={6} md={6} lg={4} className="mb-3">
              <Label>
                Usage Event Type<span className="text-danger"> *</span>
              </Label>
              <LabelText
                htmlFor={`recurringUsageRatePlan[${index}].eventType`}
                classLabelName={`d-block ms-0 rv-sub-label ${
                  (touched?.recurringUsageRatePlan &&
                    touched?.recurringUsageRatePlan?.[index] &&
                    !values?.recurringUsageRatePlan?.[index]?.eventType) ||
                  false
                    ? "rv-label-danger"
                    : "rv-label-muted"
                } `}
                displayText={
                  touched?.recurringUsageRatePlan?.[index]?.eventType &&
                  errors?.recurringUsageRatePlan?.[index]?.eventType
                    ? errors?.recurringUsageRatePlan?.[index]?.eventType
                    : "Type to create a new option"
                }
              />
              <CreatableSelectField
                values={
                  typeof values.recurringUsageRatePlan?.[index].eventType ===
                  "string"
                    ? {
                        label: values.recurringUsageRatePlan[index].eventType,
                        value: values.recurringUsageRatePlan[index].eventType,
                      }
                    : values.recurringUsageRatePlan?.[index].eventType
                }
                placeholder=" "
                handleChange={handleGrantsAndChargesRecurringFrequencyAndUsageOptions(
                  values.recurringUsageRatePlan?.[index].eventType,
                  `recurringUsageRatePlan[${index}].eventType`,
                  setFieldValue,
                  setFieldTouched
                )}
                className={
                  errors.recurringUsageRatePlan?.[index]?.eventType &&
                  touched.recurringUsageRatePlan?.[index]?.eventType
                    ? "error-input"
                    : ""
                }
                onKeyDown={(event) => {
                  const key = event.key;
                  const regex = /^[a-zA-Z0-9 ]$/;
                  if (
                    regex.test(key) ||
                    [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Enter",
                    ].includes(key)
                  ) {
                    return;
                  }
                  event.preventDefault();
                }}
                name={`recurringUsageRatePlan[${index}].eventType`}
                options={EventTypeOptions || []}
                isMulti={false}
                fieldLabel="Event Type"
                fieldName={`recurringUsageRatePlan[${index}].eventType`}
                touched={touched.recurringUsageRatePlan?.[index]?.eventType}
                isRVStyle={true}
                disabled={isSubscribed}
                onCreateOption={(newValue) => {
                  const option = { label: newValue, value: newValue };
                  handleGrantsAndChargesRecurringFrequencyAndUsageOptions(
                    values.recurringUsageRatePlan?.[index].eventType,
                    `recurringUsageRatePlan[${index}].eventType`,
                    setFieldValue,
                    setFieldTouched
                  )(option);
                  dispatch(
                    addUsageEventType({ name: newValue }, setLoading, () => {
                      dispatch(fetchAllUsageEventType(setLoading));
                    })
                  );
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={4} className="mb-3">
              <Label>
                UOM<span className="text-danger"> *</span>
              </Label>
              <LabelText
                htmlFor={`recurringUsageRatePlan[${index}].unitOfMeasurement`}
                classLabelName={`d-block ms-0 rv-sub-label ${
                  (touched?.recurringUsageRatePlan &&
                    touched?.recurringUsageRatePlan?.[index] &&
                    !values?.recurringUsageRatePlan?.[index]
                      ?.unitOfMeasurement) ||
                  false
                    ? "rv-label-danger"
                    : "rv-label-muted"
                } `}
                displayText={
                  touched?.recurringUsageRatePlan?.[index]?.unitOfMeasurement &&
                  errors?.recurringUsageRatePlan?.[index]?.unitOfMeasurement
                    ? errors?.recurringUsageRatePlan?.[index]?.unitOfMeasurement
                    : "Select Unit of Measurement"
                }
              />
              <SelectField
                name={`recurringUsageRatePlan[${index}].unitOfMeasurement`}
                placeholder={" "}
                isRVStyle={true}
                className={
                  touched.recurringUsageRatePlan &&
                  touched.recurringUsageRatePlan?.[index] &&
                  !values.recurringUsageRatePlan?.[index].unitOfMeasurement
                    ? "error-input"
                    : ""
                }
                isDisabled={isSubscribed}
                options={uomOptions}
                handleChange={handleGrantsAndChargesRecurringFrequencyAndUsageOptions(
                  values.recurringUsageRatePlan?.[index].unitOfMeasurement,
                  `recurringUsageRatePlan[${index}].unitOfMeasurement`,
                  setFieldValue,
                  setFieldTouched
                )}
                value={values.recurringUsageRatePlan?.[index].unitOfMeasurement}
                invalid={
                  errors.recurringUsageRatePlan &&
                  errors.recurringUsageRatePlan?.[index] &&
                  touched.recurringUsageRatePlan &&
                  touched.recurringUsageRatePlan?.[index] &&
                  touched.recurringUsageRatePlan?.[index].unitOfMeasurement &&
                  !values.recurringUsageRatePlan?.[index].unitOfMeasurement
                }
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={4} className="mb-3">
              <Label>
                Priority<span className="text-danger"> *</span>
              </Label>
              <LabelText
                htmlFor={`recurringUsageRatePlan[${index}].priority`}
                classLabelName={`d-block ms-0 rv-sub-label ${
                  (touched?.recurringUsageRatePlan &&
                    touched?.recurringUsageRatePlan?.[index]?.priority &&
                    errors?.recurringUsageRatePlan?.[index]?.priority &&
                    !values?.recurringUsageRatePlan?.[index]?.priority) ||
                  false
                    ? "rv-label-danger"
                    : "rv-label-muted"
                } `}
                displayText={
                  touched?.recurringUsageRatePlan?.[index]?.priority &&
                  errors?.recurringUsageRatePlan?.[index]?.priority
                    ? errors?.recurringUsageRatePlan?.[index]?.priority
                    : "Enter Priority"
                }
              />
              <InputField
                name={`recurringUsageRatePlan[${index}].priority`}
                className={`form-control ${
                  isSubscribed ? "label-disable-color-edit-offer" : ""
                }`}
                type="number"
                disabled={isSubscribed}
                handleChange={handleChangeForImsi(
                  setFieldValue,
                  `recurringUsageRatePlan[${index}].priority`
                )}
                value={values.recurringUsageRatePlan?.[index].priority}
                invalid={
                  errors.recurringUsageRatePlan &&
                  errors.recurringUsageRatePlan?.[index] &&
                  touched.recurringUsageRatePlan &&
                  touched.recurringUsageRatePlan?.[index] &&
                  touched.recurringUsageRatePlan?.[index].priority &&
                  !values.recurringUsageRatePlan?.[index].priority
                }
              />
            </Col>
          </Row>
        </TabPane>

        <TabPane tabId={"2"}>
          <BalanceImpactList
            pushRef={pushBalanceRef}
            value={val?.balanceImpacts || []}
            usageType={val?.type}
            inx={index}
          />
        </TabPane>

        <TabPane tabId={"3"}>
          <FieldArray
            name={`recurringUsageRatePlan[${index}].attributeBasedDetails`}
          >
            {({ push: pushABD, remove: removeABD }) => (
              <AttributeBasedDetailsTableRecurring
                index={index}
                tableDataRecurring={val?.attributeBasedDetails || []}
                setTableDataRecurring={(updatedData) => {
                  setFieldValue(
                    `recurringUsageRatePlan[${index}].attributeBasedDetails`,
                    updatedData
                  );
                }}
                addAttributeBasedDetail={addAttributeBasedDetail}
                validateCurrentAttributeDetails={
                  validateCurrentAttributeDetails
                }
              />
            )}
          </FieldArray>
        </TabPane>
      </TabContent>
    </div>
  );
};

export default UsageRatePlanRowRecurring;
